import React from 'react';
import placeholder from 'images/placeholder.webp';
import {
  formatInt,
  formatPrice,
  formatPricePerSqft,
} from '../../../../shared/utils/formatters/propertyFormatters';
import { Property } from '../../domain/entities/Property';
import { useBreakpoint } from '../../../../shared/hooks/useBreakpoint';
import { useHoverStore } from '../../../public-map/application/hoverStore';

export function GridCard({ property }: { property: Property }) {
  const { isXs } = useBreakpoint();
  const {
    setHoveredPropertyId,
    isPropertyHovered,
    isPropertyInHoveredCluster,
  } = useHoverStore();

  const isSingleHover =
    isPropertyHovered(property.id) && !isPropertyInHoveredCluster(property.id);
  const isInCluster = isPropertyInHoveredCluster(property.id);

  const handleMouseEnter = () => setHoveredPropertyId(property.id);
  const handleMouseLeave = () => setHoveredPropertyId(null);

  // Apply highlight classes based on hover state
  const highlightClass = isSingleHover
    ? 'ring-2 ring-indigo-500 shadow-lg'
    : isInCluster
    ? 'ring-1 ring-indigo-300 shadow-md'
    : '';
  if (isXs) {
    return (
      <article
        id="grid-card-mobile"
        className={`rounded-lg shadow-sm border bg-white flex flex-col w-full overflow-hidden items-start transition-all duration-150 ${highlightClass}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => window.open(`/properties/${property.id}`, '_blank')}
      >
        <div
          id="grid-card-content-header-address"
          className="font-semibold text-md p-2"
        >
          {property.address ?? '—'}
        </div>
        <div className="flex flex-row w-full">
          <div
            id="grid-card-content-image"
            className="flex justify-center items-center"
          >
            <img
              src={property.imgUrl ?? placeholder}
              alt={property.address ?? 'Address Unknown'}
              className="w-[175px] h-[250px] object-cover shrink-0"
            />
          </div>

          {/* Price and price per sq ft block */}
          <div
            id="grid-card-content"
            className="px-2 py-3 flex flex-col w-full gap-3 border-y-2 border-gray-200"
          >
            <div
              id="grid-card-content-price"
              className="flex flex-col justify-between items-center"
            >
              <div
                id="grid-card-content-body-left-top"
                className="flex flex-col pr-2 py-1 justify-center items-center"
              >
                <div className="text-xs text-gray-500 uppercase">
                  List Price
                </div>
                <div className="text-lg font-bold">
                  {formatPrice(property.listPrice)}
                </div>
              </div>
              <div
                id="grid-card-content-body-left-bottom"
                className="flex flex-col py-1 justify-center"
              >
                <div className="text-xs text-gray-400 uppercase">
                  Price / sq ft
                </div>
                <div className="text-lg font-bold">
                  {formatPricePerSqft(property.listPricePerSqft)}
                </div>
              </div>
            </div>

            {/* Middle block */}
            <div
              id="grid-card-content-body-middle"
              className="flex flex-col justify-center items-center py-3 border-y-2 border-gray-200"
            >
              <div className="text-xs text-gray-500 uppercase">
                Potential Equity
              </div>
              <div className="text-2xl font-bold text-gray-700 font-inter">
                {formatPrice(property.equity)}
              </div>
            </div>

            <div className="text-sm font-bold text-gray-600 flex flex-1 items-center xs:items-end justify-between">
              <div>{formatInt(property.beds)} bed</div>
              <div>{formatInt(property.baths)} bath</div>
              <div>{formatInt(property.sqft)} sqft</div>
            </div>
          </div>
        </div>
      </article>
    );
  }
  return (
    <article
      id="grid-card-desktop"
      className={`flex cursor-pointer rounded-lg shadow-sm border bg-white w-full max-w-[490px] overflow-hidden items-start transition-all duration-150 ${highlightClass}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => window.open(`/properties/${property.id}`, '_blank')}
    >
      <img
        src={property.imgUrl ?? placeholder}
        alt={property.address ?? 'Address Unknown'}
        className="w-[197px] h-[275px] object-cover shrink-0"
      />

      <div
        id="grid-card-content"
        className="px-6 py-3 flex flex-col w-full gap-3"
      >
        {/* Header */}
        <div
          id="grid-card-content-header-address"
          className="font-semibold text-md"
        >
          {property.address ?? '—'}
        </div>
        {/* Price and price per sq ft block */}
        <div
          id="grid-card-content-price"
          className="flex flex-row justify-between"
        >
          <div
            id="grid-card-content-body-left-top"
            className="flex flex-col px-2 py-1 justify-center"
          >
            <div className="text-xs text-gray-500 uppercase">List Price</div>
            <div className="text-lg font-bold">
              {formatPrice(property.listPrice)}
            </div>
          </div>
          <div
            id="grid-card-content-body-left-bottom"
            className="flex flex-col px-2 py-1 justify-center"
          >
            <div className="text-xs text-gray-400 uppercase">Price / sq ft</div>
            <div className="text-lg font-bold">
              {formatPricePerSqft(property.listPricePerSqft)}
            </div>
          </div>
        </div>

        {/* Middle block */}
        <div
          id="grid-card-content-body-middle"
          className="flex flex-col justify-center items-center py-3 border-y-2 border-gray-200"
        >
          <div className="text-xs text-gray-500 uppercase">
            Potential Equity
          </div>
          <div className="text-3xl font-bold text-gray-700 font-inter">
            {formatPrice(property.equity)}
          </div>
        </div>

        <div className="text-sm font-bold text-gray-600 flex flex-1 items-end justify-between flex-row">
          <div>{formatInt(property.beds)} beds</div>
          <div>{formatInt(property.baths)} baths</div>
          <div>{formatInt(property.sqft)} sq ft</div>
        </div>
      </div>
    </article>
  );
}
